import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Code2, Megaphone, Sparkles, Wallet,
  ShieldCheck, BrainCircuit, LogOut, Coins, Menu, X, MessageCircleHeart, Images, Film, SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { api } from '@/lib/api';

const ICONS = { Code2, Megaphone, Sparkles };

function NavItem({ to, icon: Icon, label, accent, onClick }) {
  return (
    <NavLink to={to} end={to === '/'} onClick={onClick}>
      {({ isActive }) => (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all"
          style={{
            background: isActive ? 'rgba(20,125,255,0.14)' : 'transparent',
            border: `1px solid ${isActive ? 'rgba(20,125,255,0.35)' : 'transparent'}`,
            color: isActive ? '#FFFFFF' : '#8A919D',
            boxShadow: isActive ? '0 0 24px rgba(20,125,255,0.14)' : 'none',
          }}>
          <Icon size={18} style={{ color: isActive ? accent || luxe.blue : '#8A919D' }} />
          <span className="text-sm font-semibold">{label}</span>
        </div>
      )}
    </NavLink>
  );
}

export default function Shell({ children, fluid }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState(user?.credits_balance ?? 0);
  const [credInfo, setCredInfo] = useState({ percentage: 100, low: false });
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === 'studio_admin';

  useEffect(() => {
    if (isAdmin) return;
    api('/studio-credits/balance').then((r) => { setCredits(r.balance); setCredInfo(r); }).catch(() => {});
    api('/studio-credits/announcement').then((r) => {
      const a = r.announcement;
      if (a && localStorage.getItem(`offer-dismissed-${a.id}`) !== '1') setOffer(a); else setOffer(null);
    }).catch(() => {});
  }, [location.pathname, isAdmin]);

  const dismissOffer = () => { if (offer) localStorage.setItem(`offer-dismissed-${offer.id}`, '1'); setOffer(null); };

  const doLogout = () => { logout(); navigate('/login'); };

  const sidebar = (
    <div className="h-full flex flex-col p-5" style={{ width: 264, background: luxe.obsidian, borderRight: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="flex items-center gap-3 mb-8 px-1">
        <img src="/myra-logo.png" alt="MYRA AI" className="w-11 h-11 rounded-full object-cover" style={{ boxShadow: '0 0 24px rgba(20,125,255,0.35)' }} />
        <div>
          <div className="font-display text-lg font-black leading-none" style={{ color: '#FFFFFF' }}>MYRA AI</div>
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: luxe.gold }}>Command Center</div>
        </div>
      </div>

      <div className="text-[10px] font-extrabold uppercase tracking-widest mb-2 px-2" style={{ color: luxe.muted }}>Overview</div>
      <NavItem to="/" icon={LayoutDashboard} label="Dashboard" onClick={() => setOpen(false)} />
      <NavItem to="/myra" icon={MessageCircleHeart} label="Myra Assistant" accent={luxe.pink} onClick={() => setOpen(false)} />

      <div className="text-[10px] font-extrabold uppercase tracking-widest mt-5 mb-2 px-2" style={{ color: luxe.muted }}>Pillars</div>
      {Object.values(PILLARS).map((p) => {
        const Icon = ICONS[p.icon];
        return <NavItem key={p.key} to={p.path} icon={Icon} label={p.name} accent={p.primary} onClick={() => setOpen(false)} />;
      })}

      <div className="text-[10px] font-extrabold uppercase tracking-widest mt-5 mb-2 px-2" style={{ color: luxe.muted }}>Studio</div>
      <NavItem to="/wallet" icon={Wallet} label="Credits & Wallet" accent={luxe.gold} onClick={() => setOpen(false)} />
      <NavItem to="/gallery" icon={Images} label="Gallery" accent={luxe.pink} onClick={() => setOpen(false)} />
      {isAdmin && <NavItem to="/admin" icon={ShieldCheck} label="Admin Console" accent={luxe.blue} onClick={() => setOpen(false)} />}
      {isAdmin && <NavItem to="/facebook-ai" icon={BrainCircuit} label="Facebook AI" accent={luxe.blue} onClick={() => setOpen(false)} />}
      {isAdmin && <NavItem to="/manage" icon={SlidersHorizontal} label="Manage" accent={luxe.blue} onClick={() => setOpen(false)} />}

      <div className="mt-auto pt-5">
        <div className="flex items-center gap-3 p-3 rounded-xl mb-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: gradientCss([luxe.blue, luxe.blueDeep]), color: '#fff' }}>
            {(user?.name || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: '#FFFFFF' }}>{user?.name}</div>
            <div className="text-[10px] truncate" style={{ color: luxe.muted }}>{isAdmin ? 'Studio Admin' : 'Creator'}</div>
          </div>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold" style={{ color: '#FCA5A5', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <LogOut size={16} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen flex" style={{ zIndex: 1 }}>
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">{sidebar}</div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="relative z-50">{sidebar}</div>
          <div className="flex-1" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-[264px] min-w-0">
        <div className="sticky top-0 z-20 flex items-center justify-between px-5 py-3.5" style={{ background: 'rgba(8,9,18,0.94)', backdropFilter: 'blur(18px)', borderBottom: `1px solid ${luxe.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.16)' }}>
          <button className="lg:hidden" onClick={() => setOpen(!open)} style={{ color: luxe.text }}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="hidden lg:block text-sm font-semibold" style={{ color: luxe.muted }}>
            Welcome back, <span style={{ color: luxe.text }}>{user?.name?.split(' ')[0]}</span> ✨
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(78,158,255,0.12)', border: '1px solid rgba(78,158,255,0.4)' }}>
                <ShieldCheck size={16} style={{ color: luxe.blue }} />
                <span className="font-bold text-sm" style={{ color: luxe.blue }}>Admin</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2 rounded-full" style={{ background: credInfo.low ? 'rgba(239,68,68,0.12)' : 'rgba(233,196,106,0.1)', border: `1px solid ${credInfo.low ? 'rgba(239,68,68,0.45)' : 'rgba(233,196,106,0.35)'}`, display: isAdmin ? 'none' : 'flex' }} data-testid="credits-chip">
              <Coins size={16} style={{ color: credInfo.low ? luxe.danger : luxe.gold }} />
              <span className="font-bold text-sm" style={{ color: credInfo.low ? '#FCA5A5' : undefined }}><span className={credInfo.low ? '' : 'gold-text'}>{credits}</span></span>
              <span className="hidden sm:inline w-14 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
                <span className="block h-full rounded-full" style={{ width: `${Math.max(4, credInfo.percentage || 0)}%`, background: credInfo.low ? luxe.danger : luxe.gold, transition: 'width .4s' }} />
              </span>
              <span className="text-xs font-semibold" style={{ color: luxe.muted }}>{credInfo.percentage ?? 0}%</span>

            </div>
          </div>
        </div>

        {!isAdmin && offer && (
          <div className="flex items-center justify-between gap-3 px-5 py-2.5 flex-wrap" data-testid="offer-banner"
            style={{
              background: offer.kind === 'success' ? 'rgba(14,169,114,0.12)' : offer.kind === 'warning' ? 'rgba(233,196,106,0.14)' : 'rgba(20,125,255,0.12)',
              borderBottom: `1px solid ${offer.kind === 'success' ? 'rgba(14,169,114,0.35)' : offer.kind === 'warning' ? 'rgba(233,196,106,0.4)' : 'rgba(20,125,255,0.35)'}`,
            }}>
            <span className="text-sm" style={{ color: luxe.text }}>
              <b style={{ color: offer.kind === 'success' ? luxe.success : offer.kind === 'warning' ? luxe.gold : luxe.blue }}>{offer.title}</b>
              {offer.message ? <> — {offer.message}</> : null}
            </span>
            <button onClick={dismissOffer} data-testid="offer-dismiss" style={{ color: luxe.muted }}><X size={16} /></button>
          </div>
        )}

        {!isAdmin && credInfo.low && (
          <div className="flex items-center justify-between gap-3 px-5 py-2.5 flex-wrap" style={{ background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.3)' }} data-testid="low-credit-banner">
            <span className="text-sm font-semibold" style={{ color: luxe.danger }}>
              ⚠️ You're low on credits — only <b>{credits}</b> left ({credInfo.percentage}% remaining).
            </span>
          </div>
        )}

        <div className={fluid ? '' : 'p-5 sm:p-8 max-w-[1200px] mx-auto'}>{children}</div>
      </div>
    </div>
  );
}
